/**
 * AttachedHighlightsContext — Cross-page pinned text-selection store.
 *
 * Why this exists:
 *   The site-wide `GlobalSelectionToolbar` lets the user highlight any text
 *   anywhere in the app and click "Ask". That action needs to drop the
 *   highlighted snippet into the Ask Argus chat composer as an attached
 *   quote (pattern matches ChatGPT/Claude "use this text" affordances).
 *
 *   The toolbar is mounted at the very root of the app, but the composer
 *   only exists on `/ask-argus/*` routes. We need a single shared queue
 *   so the toolbar (producer) and composer (consumer) can talk without
 *   passing props through the entire tree.
 *
 * Behaviour:
 *   - `add` pushes a highlight onto the queue with an auto-generated id.
 *   - Multiple highlights accumulate (user can pin several before asking).
 *   - `remove` drops a single chip via its id (X button on the chip).
 *   - `clear` empties the queue (called by composer after sending).
 *   - `formatForSubmit` returns a markdown blockquote prefix the composer
 *     prepends to the user's typed message. Pure function — easy to test.
 *
 * Persistence: none. Highlights are session-only and clear on reload.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface AttachedHighlight {
  /** Stable id used by React lists and remove(). */
  id: string;
  /** The selected text, trimmed. */
  text: string;
  /** Pathname of the page the highlight was captured on (no host). */
  sourcePath: string;
}

interface AttachedHighlightsContextValue {
  highlights: AttachedHighlight[];
  add: (text: string, sourcePath: string) => void;
  remove: (id: string) => void;
  clear: () => void;
}

const AttachedHighlightsContext =
  createContext<AttachedHighlightsContextValue | null>(null);

function makeId(): string {
  // crypto.randomUUID is available in all modern browsers — no need to
  // pull in a uuid library just for this.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `h-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function AttachedHighlightsProvider({ children }: { children: ReactNode }) {
  const [highlights, setHighlights] = useState<AttachedHighlight[]>([]);

  const add = useCallback((text: string, sourcePath: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setHighlights((prev) => [
      ...prev,
      { id: makeId(), text: trimmed, sourcePath },
    ]);
  }, []);

  const remove = useCallback((id: string) => {
    setHighlights((prev) => prev.filter((h) => h.id !== id));
  }, []);

  const clear = useCallback(() => {
    setHighlights([]);
  }, []);

  const value = useMemo<AttachedHighlightsContextValue>(
    () => ({ highlights, add, remove, clear }),
    [highlights, add, remove, clear],
  );

  return (
    <AttachedHighlightsContext.Provider value={value}>
      {children}
    </AttachedHighlightsContext.Provider>
  );
}

/**
 * Hook to consume the queue. Returns a noop-safe shape when no provider
 * is mounted (e.g. on the login page), so the global selection toolbar
 * can call `add()` unconditionally without crashing.
 */
export function useAttachedHighlights(): AttachedHighlightsContextValue {
  const ctx = useContext(AttachedHighlightsContext);
  if (ctx) return ctx;
  return {
    highlights: [],
    add: () => {},
    remove: () => {},
    clear: () => {},
  };
}

/**
 * Pure formatter — given the pending highlights and the user's typed
 * message, build the final message body that goes to the backend.
 *
 * Format:
 *   > Snippet 1
 *
 *   > Snippet 2
 *
 *   <user's typed text>
 *
 * When no highlights are pinned, returns the user's text untouched so
 * regular sends are unaffected. Exported so we can unit-test it.
 */
export function formatHighlightsForSubmit(
  highlights: AttachedHighlight[],
  userText: string,
): string {
  if (highlights.length === 0) return userText;
  const quoted = highlights
    .map((h) => {
      // Each line of the snippet is prefixed with "> " so multi-line
      // selections render as a single blockquote in markdown.
      const lines = h.text.split('\n').map((l) => `> ${l}`).join('\n');
      return lines;
    })
    .join('\n\n');
  return `${quoted}\n\n${userText}`;
}
